import { Ban, ChevronRight, CreditCard, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { useAuth } from '@/hooks/auth/useAuth';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Spinner } from '@reflex/ui/components/shared/Spinner';

const SUPPORT_EMAIL = 'support@runloop.ai';

/**
 * Full-viewport screen rendered when the active organization is
 * disabled. Replaces the org shell so members can't mount org-scoped
 * pages while the org is locked.
 *
 * - The `nonpayment` variant (billing suspended the org after dunning
 *   exhausted) explains the unpaid balance and, when the billing plugin
 *   contributes a cure path (`billingPath`), links to it so the org can
 *   pay its way back in.
 * - Members with at least one OTHER active org get a one-click
 *   switcher (no API call required; the org switcher just rewrites
 *   the active-org cookie/localstorage and reloads).
 * - Members whose only org is the disabled one fall back to a
 *   contact-support message.
 *
 * The server also rejects every authenticated API call whose active
 * org resolves to a disabled org with `403 organization_disabled`, so
 * this screen is the user-facing half of that gate.
 */
export function OrganizationDisabledScreen({
  variant = 'manual',
  billingPath = null,
  onContinueWithSso,
  connectingSso = false,
}: {
  /** Why the org is blocked: an admin action or a billing suspension. */
  variant?: 'manual' | 'nonpayment' | 'sso';
  /** Org-scoped path of the billing cure page, when the plugin is installed. */
  billingPath?: string | null;
  onContinueWithSso?: () => void;
  connectingSso?: boolean;
} = {}) {
  const { activeMembership, memberships, setActiveOrganizationId, logout } = useAuth();

  const otherActiveOrgs = memberships.filter(
    (m) =>
      m.organization.id !== activeMembership?.organization.id &&
      // Filter out any org that's currently blocked (manual disable or an
      // expired subscription), both of which carry a computed disabledReason.
      m.organization.disabledReason == null,
  );

  const orgName = activeMembership?.organization.name;
  const title =
    variant === 'sso'
      ? 'Your company requires SSO'
      : variant === 'nonpayment'
        ? orgName
          ? `${orgName} is suspended for an unpaid balance`
          : 'This organization is suspended for an unpaid balance'
        : orgName
          ? `${orgName} is disabled`
          : 'This organization is disabled';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Alert variant={variant === 'sso' ? 'default' : 'destructive'} className="max-w-md">
        {variant === 'sso' ? <ShieldCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="space-y-4">
          {variant === 'sso' && (
            <p>
              Sign in through {orgName ?? 'your organization'}&apos;s identity provider to continue.
              Your current session remains available for organizations with different sign-in rules.
            </p>
          )}
          {variant === 'sso' && onContinueWithSso && (
            <Button
              type="button"
              size="sm"
              className="w-full"
              onClick={onContinueWithSso}
              disabled={connectingSso}
            >
              {connectingSso ? <Spinner className="size-4" /> : null}
              Continue with company SSO
            </Button>
          )}
          {variant === 'nonpayment' && (
            <p>
              Payment for this organization failed and its balance is past due. Settle the balance
              to restore access.
            </p>
          )}
          {variant === 'nonpayment' && billingPath && (
            <Button asChild size="sm" data-testid="org-disabled-fix-payment">
              <Link to={billingPath}>
                <CreditCard className="h-4 w-4" />
                Fix payment
              </Link>
            </Button>
          )}
          {otherActiveOrgs.length > 0 ? (
            <>
              <p>
                Switch to one of your other organizations to keep working, or email{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="font-medium underline underline-offset-2"
                >
                  {SUPPORT_EMAIL}
                </a>{' '}
                to ask for this organization to be re-enabled.
              </p>
              <div className="flex flex-col gap-2">
                {otherActiveOrgs.map((m) => (
                  <Button
                    key={m.organization.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`Switch to ${m.organization.name}`}
                    data-testid={`org-switch-${m.organization.id}`}
                    className="justify-between"
                    onClick={() => {
                      setActiveOrganizationId(m.organization.id);
                      // Bounce the SPA so every hook re-reads the new
                      // active org and the disabled gate falls away.
                      window.location.assign('/');
                    }}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <UserAvatar
                        userName={m.organization.name}
                        avatarUrl={m.organization.avatarUrl ?? undefined}
                        size="sm"
                        className="shrink-0"
                      />
                      <span className="truncate">{m.organization.name}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  </Button>
                ))}
              </div>
            </>
          ) : (
            <p>
              {variant === 'sso'
                ? 'Sign in with company SSO above, or email '
                : variant === 'nonpayment'
                  ? 'If you believe this is a mistake, please email '
                  : 'This organization has been disabled. To get it re-enabled, please email '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium underline underline-offset-2"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void logout();
            }}
          >
            Sign out
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
